import React from "react";
import { Form, Input, Select, Button, Tooltip, Checkbox, Space, message } from "antd";
import { PlusOutlined, DeleteOutlined, FormOutlined } from "@ant-design/icons";
import { formatCPF, validateCPFRule } from "../../util/cpf";
import { useTranslation } from "react-i18next";
import { removePendingSigner } from "../../store/assinatura";
import useDispatchWithResult from "../../hooks/useDispatchWithResult";
import styles from "./_styles.less";

const SIGNATURE_TYPES = [
  { value: "PADRAO", label: "Padrão" },
  { value: "BIOMETRICA", label: "Biométrica" },
  { value: "CERTIFICADO", label: "Certificado" },
];

const SignatariosList = ({ form }) => {
  const { t } = useTranslation();
  const dispatch = useDispatchWithResult();
  const [editingItems, setEditingItems] = React.useState(new Set());

  const toggleEditClick = (fieldKey) => {
    setEditingItems((prev) => {
      const newSet = new Set(prev);
      newSet.has(fieldKey) ? newSet.delete(fieldKey) : newSet.add(fieldKey);
      return newSet;
    });
  };

  const handleRemove = (field, signer, remove) => {
    if (signer?.id) {
      dispatch(removePendingSigner(signer.id));
    }

    remove(field.name);

    setEditingItems((prev) => {
      const newSet = new Set(prev);
      newSet.delete(field.key);
      return newSet;
    });

    message.success(t("Signatário removido"));
  };

  return (
    <Form.List name="assinantes">
      {(fields, { add, remove }) => (
        <>
          <div className={styles["table-header"]}>
            <div>{t("Nome")}</div>
            <div>{t("Email")}</div>
            <div>{t("CPF")}</div>
            <div>{t("Tipo de assinatura")}</div>
            <div style={{ justifySelf: "center" }}>{t("Ações")}</div>
          </div>

          {fields.length === 0 ? (
            <div className={styles["table-empty"]}>Nenhum signatário adicionado</div>
          ) : (
            fields.map((field, index) => {
              const currentSigners = form.getFieldValue("assinantes") || [];
              const signer = currentSigners[index] || {};
              const isRegistered = !!signer.registered;
              const isEditing = editingItems.has(field.key);
              const isDisabled = isRegistered && !isEditing;
              const key = `signer-${field.key}`;

              return (
                <div key={key} className={styles["table-content"]}>
                  <Form.Item
                    name={[field.name, "nome"]}
                    rules={[
                      {
                        required: true,
                        message: t("Por favor, insira o nome"),
                      },
                    ]}
                  >
                    <Input placeholder={t("Nome")} disabled={isDisabled} autoComplete="off" />
                  </Form.Item>

                  <Form.Item
                    name={[field.name, "email"]}
                    rules={[
                      {
                        required: true,
                        message: t("Por favor, insira o email"),
                      },
                      { type: "email", message: t("Email inválido") },
                    ]}
                  >
                    <Input placeholder={t("Email")} disabled={isDisabled} autoComplete="off" />
                  </Form.Item>

                  <Form.Item
                    name={[field.name, "cpf"]}
                    rules={[
                      {
                        validator: (_, value) => {
                          if (!value || value.trim() === "") return Promise.resolve();

                          return validateCPFRule(t)(_, value);
                        },
                      },
                    ]}
                    getValueFromEvent={(e) => String(e.target.value).replace(/\D/g, "")}
                  >
                    <Input
                      placeholder={t("CPF")}
                      maxLength={14}
                      disabled={isDisabled}
                      autoComplete="off"
                      onBlur={(e) => {
                        form.setFieldValue(["assinantes", index, "cpf"], formatCPF(e.target.value));
                      }}
                    />
                  </Form.Item>

                  <Form.Item name={[field.name, "tipoAssinatura"]} initialValue="PADRAO">
                    <Select placeholder={t("Selecione")}>
                      {SIGNATURE_TYPES.map((type) => (
                        <Select.Option key={type.value} value={type.value}>
                          {t(type.label)}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Space style={{ justifyContent: "center" }}>
                    {isRegistered ? (
                      <Tooltip title={t("Atualizar Signatário")}>
                        <Form.Item name={[field.name, "add_signatario"]} style={{ marginBottom: 0 }}>
                          <Button
                            type="text"
                            icon={<FormOutlined style={{ fontSize: 15, cursor: "pointer", color: isEditing && "#1890ff", transition: "color 0.3s ease" }} />}
                            onClick={() => {
                              toggleEditClick(field.key);
                            }}
                          />
                        </Form.Item>
                      </Tooltip>
                    ) : (
                      <Tooltip title={t("Cadastrar Signatário")}>
                        <Form.Item name={[field.name, "add_signatario"]} valuePropName="checked" style={{ marginBottom: 0 }}>
                          <Checkbox defaultChecked={false} />
                        </Form.Item>
                      </Tooltip>
                    )}

                    <Tooltip title={t("Excluir")}>
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined style={{ fontSize: 15, cursor: "pointer" }} />}
                        onClick={() => {
                          handleRemove(field, signer, remove);
                        }}
                      />
                    </Tooltip>
                  </Space>
                </div>
              );
            })
          )}
          <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />} style={{ width: "15%", fontSize: 12 }}>
            {t("Adicionar signatário")}
          </Button>
        </>
      )}
    </Form.List>
  );
};

export default SignatariosList;
